import React, { useMemo } from 'react';
import { flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { Loader2 } from 'lucide-react';

import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'shadcn/components/ui/Table';
import dayjs from 'utils/dayjsConfig';

const EMPTY_VALUE = '—';

const formatNullableValue = (value) => {
    if (value == null || value === '') {
        return EMPTY_VALUE;
    }
    return String(value);
};

const renderCreatedAtCell = ({ getValue }) => {
    const value = getValue();
    if (!value) {
        return <span className="text-muted-foreground">{EMPTY_VALUE}</span>;
    }
    return dayjs(value).format('MM/DD/YYYY h:mm a');
};

const renderNullableCell = ({ getValue }) => {
    const formatted = formatNullableValue(getValue());
    if (formatted === EMPTY_VALUE) {
        return <span className="text-muted-foreground">{EMPTY_VALUE}</span>;
    }
    return formatted;
};

const MemoizedCatalogEventRow = React.memo(({ row }) => (
    <TableRow>
        {row.getVisibleCells().map((cell) => (
            <TableCell key={cell.id} className="whitespace-normal break-words text-left">
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
            </TableCell>
        ))}
    </TableRow>
));

MemoizedCatalogEventRow.displayName = 'MemoizedCatalogEventRow';

/**
 * @param {Object} props
 * @param {Array<Record<string, unknown>>} [props.data=[]]
 * @param {string} [props.emptyMessage='No events found.']
 * @param {boolean} [props.isFetching=false]
 * @param {boolean} [props.isLoading=false]
 * @param {number} [props.limit]
 * @param {string} [props.loadingMessage='Loading events…']
 * @param {boolean} [props.showItemMasterId=false]
 * @param {string} props.title
 */
export function CatalogEventsTable({
    data = [],
    emptyMessage = 'No events found.',
    isFetching = false,
    isLoading = false,
    limit,
    loadingMessage = 'Loading events…',
    showItemMasterId = false,
    title,
}) {
    const tableData = useMemo(() => (Array.isArray(data) ? data : []), [data]);

    const columns = useMemo(() => {
        const cols = [
            {
                accessorKey: 'createdAt',
                cell: renderCreatedAtCell,
                header: 'Created At',
            },
        ];

        if (showItemMasterId) {
            cols.push({
                accessorKey: 'itemMasterID',
                cell: renderNullableCell,
                header: 'Item Master ID',
            });
        }

        cols.push(
            {
                accessorKey: 'eventType',
                cell: renderNullableCell,
                header: 'Event Type',
            },
            {
                accessorKey: 'details',
                cell: renderNullableCell,
                header: 'Details',
            },
            {
                accessorKey: 'updatedBy',
                cell: renderNullableCell,
                header: 'Updated By',
            },
        );

        return cols;
    }, [showItemMasterId]);

    const table = useReactTable({
        columns,
        data: tableData,
        getCoreRowModel: getCoreRowModel(),
        getRowId: (row, index) =>
            row?.id != null ? String(row.id) : `${row?.createdAt ?? 'event'}-${row?.eventType ?? index}-${index}`,
    });

    const showLimitCaption = !isLoading && Number.isInteger(limit) && limit > 0;

    return (
        <section className="flex flex-col gap-2" aria-label={title}>
            <div className="flex flex-col gap-1 sm:flex-row sm:items-baseline sm:justify-between">
                <h3 className="text-base font-semibold">{title}</h3>
                {showLimitCaption ? (
                    <p className="text-xs text-muted-foreground">Showing latest {limit} events</p>
                ) : null}
            </div>
            {isFetching && !isLoading ? (
                <div className="h-1 w-full overflow-hidden rounded bg-muted">
                    <div className="h-full w-1/3 animate-pulse bg-primary/40" />
                </div>
            ) : null}
            <div className="flex min-h-[200px] flex-col">
                {isLoading ? (
                    <div className="flex min-h-[200px] items-center justify-center p-6">
                        <div className="flex flex-col items-center gap-3">
                            <Loader2 className="h-10 w-10 animate-spin text-muted-foreground" />
                            <span className="text-sm text-muted-foreground">{loadingMessage}</span>
                        </div>
                    </div>
                ) : (
                    <Table className="max-h-[420px] overflow-y-auto rounded-md border">
                        <TableHeader>
                            {table.getHeaderGroups().map((headerGroup) => (
                                <TableRow key={headerGroup.id}>
                                    {headerGroup.headers.map((header) => (
                                        <TableHead
                                            key={header.id}
                                            className="sticky top-0 z-10 whitespace-normal break-words bg-background text-left shadow-[inset_0_-1px_0_0_hsl(var(--border))]"
                                        >
                                            {header.isPlaceholder
                                                ? null
                                                : flexRender(header.column.columnDef.header, header.getContext())}
                                        </TableHead>
                                    ))}
                                </TableRow>
                            ))}
                        </TableHeader>
                        <TableBody>
                            {table.getRowModel().rows.length ? (
                                table
                                    .getRowModel()
                                    .rows.map((row) => <MemoizedCatalogEventRow key={row.id} row={row} />)
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={columns.length} className="h-24 text-center">
                                        {emptyMessage}
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                )}
            </div>
        </section>
    );
}

CatalogEventsTable.displayName = 'CatalogEventsTable';
